import React, { useState, useEffect, useRef, useCallback } from 'react';
import Map from '../components/Map/map';
import LineChart from '../components/LineChart';
import Navbar from "../components/navbar";
import { useTranslation } from "react-i18next";
import DownloadData from '../components/downloadData';
import GraphCustomizationPanel from '../components/GraphCustomizationPanel';
import ErrorBoundary from '../components/ErrorBoundary';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { Cog6ToothIcon, ChevronLeftIcon, ChevronRightIcon, CameraIcon } from '@heroicons/react/24/outline';
import AIChat from '../components/AIChat';

const MapPage = () => {
  const { t } = useTranslation();
  const [filteredData, setFilteredData] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [startDate, setStartDate] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [endDate, setEndDate] = useState("");

  const chartContainerRef = useRef(null);
  
  // Satellite data state  
  const [satelliteData, setSatelliteData] = useState([]);
  const [selectedToxin, setSelectedToxin] = useState("");

  // Graph customization state
  const [showCustomizationPanel, setShowCustomizationPanel] = useState(false);
  const [customizationSettings, setCustomizationSettings] = useState(null);
  
  // Memoize the settings change handler
  const handleSettingsChange = useCallback((settings) => {
    setCustomizationSettings(settings);
  }, []);

  // Available locations for dropdown (independent from map)
  const [availableLocations, setAvailableLocations] = useState([]);

  // Map visibility state
  const [isMapVisible, setIsMapVisible] = useState(true);

  // Function to fetch satellite data for any location
  const fetchSatelliteData = useCallback(async (locationName, locationId) => {
    if (!locationId) {
      setSatelliteData([]);
      return;
    }
    try {
      // Production API configuration using environment variables
      const apiBase = process.env.REACT_APP_LOCATIONS_API?.replace('/locations', '') ||
                     process.env.REACT_APP_MEASUREMENTS_API?.replace('/measurements', '') ||
                     'http://localhost:8000'; // Fallback for local development
      
      // Construct the database URL properly - insert path before existing query params
      const [baseUrl, queryParams] = apiBase.split('?');
      const satelliteUrl = queryParams
        ? `${baseUrl}/satellite/data/${locationId}?${queryParams}&limit=1000`
        : `${baseUrl}/satellite/data/${locationId}?limit=1000`;

      const dateParams = [];
      if (startDate) dateParams.push(`start_date=${startDate}`);
      if (endDate) dateParams.push(`end_date=${endDate}`);

      const finalUrl = dateParams.length > 0
        ? `${satelliteUrl}&${dateParams.join('&')}`
        : satelliteUrl;

      const response = await fetch(finalUrl);
      if (response.ok) {
        const data = await response.json();
        setSatelliteData(data.data || []);
      } else {
        setSatelliteData([]);
      }
    } catch (error) {
      // Production: Satellite data fetch error
      setSatelliteData([]);
    }
  }, [startDate, endDate]);

  // NOTE: renamed second param from ctrlKey -> multi for clarity
  const handleSelectLocation = useCallback(async (loc, multi) => {
    let shouldFetchSatelliteData = false;
    let selectedLocationId = null;
    setSelectedLocations(prev => {
      const exists = prev.some(x => x.name === loc.name && x.date === loc.date);

      if (multi) {
        if (exists) {
          // deselect
          return prev.filter(x => !(x.name === loc.name && x.date === loc.date));
        } else {
          if (prev.length >= 3) return prev;
          shouldFetchSatelliteData = true;
          selectedLocationId = loc.id;
          return [...prev, loc];
        }
      } else {
        if (exists && prev.length === 1) {
          return prev; // no-op
        } else {
          shouldFetchSatelliteData = true;
          selectedLocationId = loc.id;
          return [loc];
        }
      }
    });

    if (shouldFetchSatelliteData && selectedLocationId) {
      await fetchSatelliteData(loc.name, selectedLocationId);
    } else if (!shouldFetchSatelliteData) {
      setSatelliteData([]);
    }
  }, [fetchSatelliteData]);

  useEffect(() => {
    if (selectedLocations.length > 0) {
      const firstLocation = selectedLocations[0];
      fetchSatelliteData(firstLocation.name, firstLocation.id);
    }
  }, [startDate, endDate, fetchSatelliteData, selectedLocations]);

  const handleDateRange = useCallback((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  const handleFilteredDataChange = useCallback((data) => {
    setFilteredData(data);
    
    // Extract unique locations from filtered data for dropdown
    const uniqueLocs = [];
    const seen = new Set();
    data.forEach(item => {
      const locId = item.properties.id;
      if (!seen.has(locId)) {
        seen.add(locId);
        uniqueLocs.push({
          id: locId,
          name: item.properties.name
        });
      }
    });
    setAvailableLocations(uniqueLocs);
  }, []);

  const combinedFilteredData = selectedLocations.length > 0
    ? filteredData.filter(d =>
        selectedLocations.some(loc => d.properties.name === loc.name) &&
        (selectedToxin === "" || d.properties.toxin === selectedToxin)
      )
    : [];

  const selectedNames = selectedLocations.map(loc => loc.name).join(', ');

  const filteredSatelliteData = satelliteData.filter(item => {
    if (!startDate || !endDate) return true;
    const itemDate = new Date(item.date);
    const start = new Date(startDate);
    const end = new Date(endDate);
    return itemDate >= start && itemDate <= end;
  });

  const getUniqueToxins = () => {
    const toxinset = new Set();
    if (filteredData.length === 0) return [];
    filteredData.forEach(d => {
      const toxin = d.properties?.toxin || d.properties?.toxin_type;
      if (toxin && toxin.trim() !== "") toxinset.add(toxin);
    });
    return Array.from(toxinset);
  };

  const [chartDimensions, setChartDimensions] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const resizeChart = () => {
      if (chartContainerRef.current) {
        setChartDimensions({
          width: chartContainerRef.current.offsetWidth,
          height: chartContainerRef.current.offsetHeight,
        });
      }
    };
    window.addEventListener('resize', resizeChart);
    resizeChart();
    return () => window.removeEventListener('resize', resizeChart);
  }, []);

  const handleRemoveLast = useCallback(() => {
    setSelectedLocations(prev => prev.slice(0, -1));
  }, []);

  // Handle map resize when visibility changes
  useEffect(() => {
    // Give the DOM time to finish the transition, then resize map
    const timer = setTimeout(() => {
      // Trigger a window resize event that Mapbox listens to
      window.dispatchEvent(new Event('resize'));
    }, 350); // Slightly longer than the 300ms transition
    
    return () => clearTimeout(timer);
  }, [isMapVisible]);

  // Handle location selection from dropdown (independent of map)
  const handleDropdownLocationSelect = useCallback((locationId) => {
    if (!locationId) return;
    
    // Find the location in filtered data
    const locationData = filteredData.find(d => d.properties.id === parseInt(locationId));
    if (!locationData) return;

    const loc = {
      id: locationData.properties.id,
      name: locationData.properties.name,
      date: locationData.properties.date
    };

    setSelectedLocations(prev => {
      // Check if already selected
      const exists = prev.some(x => x.id === loc.id);
      if (exists) return prev;
      
      // Limit to 3 locations
      if (prev.length >= 3) {
        alert(t('map_page.dashboard.max_locations_alert'));
        return prev;
      }
      
      return [...prev, loc];
    });

    // Fetch satellite data for this location
    fetchSatelliteData(loc.name, loc.id);
  }, [filteredData, fetchSatelliteData, t]);

  const handleDownload = ({ startDate, endDate, selectedToxin, format }) => {
    const filtered = combinedFilteredData.filter(item => {
      const itemDate = new Date(item.properties.date || item.date);
      const start = new Date(startDate);
      const end = new Date(endDate);
      const toxin = item.properties?.toxin || item.properties?.toxin_type || "";
      return (
        itemDate >= start &&
        itemDate <= end &&
        (selectedToxin === "" || toxin === selectedToxin)
      );
    });

    if (format === "json") {
      const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: "application/json" });
      saveAs(blob, "report.json");
    } else {
      const csvHeader = Object.keys(filtered[0]?.properties || {}).join(",");
      const csvRows = filtered.map(d => Object.values(d.properties).join(","));
      const csvContent = [csvHeader, ...csvRows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, "report.csv");
    }
  };

  const handleDownloadGraph = useCallback(async () => {
    if (chartContainerRef.current) {
      try {
        const canvas = await html2canvas(chartContainerRef.current, {
          backgroundColor: '#ffffff',
          scale: 2 // higher resolution
        });
        
        canvas.toBlob((blob) => {
          saveAs(blob, `pollution-trends-${new Date().toISOString().split('T')[0]}.png`);
        });
      } catch (error) {
        console.error('Error downloading graph:', error);
        alert(t('map_page.errors.download_failed'));
      }
    }
  }, [t]);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Navbar />
      <div className="flex flex-col lg:flex-row flex-1 overflow-hidden pt-20 relative">
        {/* Map Section - Full screen on mobile, half on desktop */}
        <div className={`${
          isMapVisible ? 'w-full lg:w-1/2' : 'w-0 lg:w-0'
        } h-full bg-white shadow-md transition-all duration-300 ease-in-out overflow-hidden relative`}>
          <div className={`${isMapVisible ? 'block' : 'hidden'} w-full h-full`}>
            <ErrorBoundary
              errorTitle={t('map_page.errors.map_unavailable')}
              errorMessage={t('map_page.errors.map_unavailable_message')}
              fallback={
                <div className="flex items-center justify-center h-full bg-gray-100">
                  <div className="text-center p-6">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('map_page.errors.map_unavailable')}</h3>
                    <p className="text-gray-500 text-sm mb-2">{t('map_page.errors.map_currently_unavailable')}</p>
                    <p className="text-gray-600 text-xs">{t('map_page.errors.use_dropdown')}</p>
                  </div>
                </div>
              }
            >
              <Map
                onSelectLocation={(loc, multi) => handleSelectLocation(loc, multi)}
                onDateRangeChange={handleDateRange}
                onFilteredDataChange={handleFilteredDataChange}
              />
            </ErrorBoundary>
          </div>
        </div>

        {/* Toggle Map Button - Always visible on desktop */}
        <button
          onClick={() => setIsMapVisible(!isMapVisible)}
          className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 z-50 items-center justify-center w-8 h-20 bg-white hover:bg-gray-100 border border-gray-300 shadow-lg transition-all hover:shadow-xl rounded-r-lg"
          style={{ left: isMapVisible ? 'calc(50% - 1rem)' : '0' }}
          title={isMapVisible ? t('map_page.controls.hide_map') : t('map_page.controls.show_map')}
        >
          {isMapVisible ? (
            <ChevronLeftIcon className="w-5 h-5 text-gray-700" />
          ) : (
            <ChevronRightIcon className="w-5 h-5 text-gray-700" />
          )}
        </button>

        {/* Unified Dashboard Section - Hidden on mobile, shown on desktop */}
        <div className={`${
          isMapVisible ? 'w-full lg:w-1/2' : 'w-full'
        } hidden lg:flex lg:h-full flex-col p-4 md:p-6 lg:p-8 overflow-y-auto transition-all duration-300 ease-in-out mt-5`}>
          {/* Location Selector */}
          <div className="mb-6">
            <label htmlFor="locationDropdown" className="block text-sm font-medium text-gray-700 mb-2">
              {t('map_page.dashboard.select_location')}
            </label>
            <select
              id="locationDropdown"
              onChange={(e) => handleDropdownLocationSelect(e.target.value)}
              value=""
              className="block w-full px-4 py-2.5 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors"
            >
              <option value="">{t('map_page.dashboard.choose_location')}</option>
              {availableLocations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          {selectedLocations.length > 0 ? (
            <div className={`w-full mx-auto space-y-6 pb-8 ${isMapVisible ? 'max-w-3xl' : 'max-w-[95%] px-4'}`}>
              <h2 className="text-2xl font-semibold text-gray-800">
                {t("map_page.dashboard.title", { names: selectedNames })}
              </h2>

              {/* Toxin Filter */}
              <div className="mb-4">
                <label htmlFor="toxinSelect" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('map_page.dashboard.filter_by_toxin')}
                </label>
                <select
                  id="toxinSelect"
                  value={selectedToxin}
                  onChange={(e) => setSelectedToxin(e.target.value)}
                  className="block w-60 px-4 py-2.5 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors"
                >
                  <option value="">{t('map_page.dashboard.all_toxins')}</option>
                  {getUniqueToxins().map((toxin) => (
                    <option key={toxin} value={toxin}>{toxin}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  onClick={handleRemoveLast}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={selectedLocations.length === 0}
                >
                  {t('map_page.dashboard.remove_last')}
                </button>
                <button
                  onClick={() => setShowDownloadModal(true)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
                >
                  {t('map_page.dashboard.download_data')}
                </button>
                <button
                  onClick={() => setShowCustomizationPanel(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center gap-2"
                >
                  <Cog6ToothIcon className="w-4 h-4" />
                  {t('map_page.dashboard.customize')}
                </button>
                <button
                  onClick={handleDownloadGraph}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center gap-2"
                  title={t('map_page.dashboard.save_graph')}
                >
                  <CameraIcon className="w-4 h-4" />
                  {t('map_page.dashboard.save_graph')}
                </button>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
                <h3 className="text-lg font-medium text-gray-700 mb-4">
                  {t("map_page.dashboard.trends")}
                </h3>

                <ErrorBoundary
                  errorTitle={t('map_page.errors.graph_unavailable')}
                  errorMessage={t('map_page.errors.graph_unavailable_message')}
                >
                  <div ref={chartContainerRef} className={`flex justify-center w-full ${isMapVisible ? 'h-[400px]' : 'h-[600px]'} pb-6`}>
                    <div className="w-full h-full">
                      <LineChart
                        data={combinedFilteredData}
                        cityNames={selectedLocations.map(loc => loc.name)}
                        satelliteData={filteredSatelliteData}
                        locationName={selectedNames}
                        width={chartDimensions.width}
                        height={Math.max(chartDimensions.height, isMapVisible ? 400 : 600)}
                        selectedToxin={selectedToxin}
                        customizationSettings={customizationSettings}
                      />
                    </div>
                  </div>
                </ErrorBoundary>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center py-12">
              <div className="max-w-md p-8 bg-white rounded-lg shadow-sm border border-gray-200">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('map_page.dashboard.no_location_selected')}</h2>
                <p className="text-gray-600 text-sm">
                  {t('map_page.dashboard.select_location_prompt')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <DownloadData
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownload}
        availableToxins={getUniqueToxins()}
        defaultStart={startDate}
        defaultEnd={endDate}
      />
      <AIChat />

      <GraphCustomizationPanel
        isOpen={showCustomizationPanel}
        onClose={() => setShowCustomizationPanel(false)}
        onSettingsChange={handleSettingsChange}
        locationNames={selectedLocations.map(loc => loc.name)}
      />
    </div>
  );
};

export default MapPage;
